import type { AchievementRecord, RoleRecord } from './api';

/**
 * Pure card-grouping logic for the Refine tab's corpus canvas — no fetch, no
 * React, no DOM. Mirrors apps/web/src/pages/Corpus.tsx's groupByRole (L1714-
 * 1735) and sortedRoles (L362-370) exactly in SHAPE (bucket achievements by
 * roleId, sort within a bucket by sortOrder, order roles current-first-then-
 * reverse-chronological) so the Throughline canvas groups identically to the
 * proven web canvas. Kept as its own module (not inlined in the page) so the
 * grouping can be unit-tested without mounting a component — apps/web put the
 * equivalent logic straight in Corpus.tsx; extracting it here is a deliberate
 * improvement for testability, not a departure from the grouping RULES.
 */

export interface RoleGroup {
  role: RoleRecord | null;
  items: AchievementRecord[];
}

/** Server-side achievements are already filtered to non-superseded rows
 *  (corpus.routes.ts:1406); this only additionally excludes soft-merged
 *  duplicates ('merged' status — the rewrite loop's grey-out state), matching
 *  how apps/web's canvas treats status server-wide (AchievementRecord.status
 *  doc comment, api.ts:118-120: "Merged cards render greyed... excluded from
 *  dup review + the distilled derive"). A card with an unrecognised/missing
 *  status is treated as active (fail-open to VISIBLE, never silently drop a
 *  real card the user has not merged). */
function isLiveCard(a: AchievementRecord): boolean {
  return a.status !== 'merged' && a.status !== 'archived';
}

/** A user-archived card (W3): status='archived'. Renders greyed in the archived
 *  section with an Undo, never in the live canvas. */
export function isArchivedCard(a: AchievementRecord): boolean {
  return a.status === 'archived';
}

/** A user-archived role (W3): archiveRole sets metadata.archived=true. A NULL
 *  metadata or absent/false key reads as live (mirrors the server predicate). */
export function isArchivedRole(r: RoleRecord): boolean {
  const meta = r.metadata as { archived?: unknown } | null;
  return meta?.archived === true;
}

/** The live roles for the canvas — everything not user-archived. */
export function activeRoles(roles: RoleRecord[]): RoleRecord[] {
  return roles.filter((r) => !isArchivedRole(r));
}

/** The archived roles (W3) — for the collapsed archived section. */
export function archivedRoles(roles: RoleRecord[]): RoleRecord[] {
  return roles.filter(isArchivedRole);
}

/** The archived cards (W3) — for the collapsed archived section. */
export function archivedCards(achievements: AchievementRecord[]): AchievementRecord[] {
  return achievements.filter(isArchivedCard);
}

/** Bucket achievements by their roleId (null = unattached/orphaned), sorted
 *  within each bucket by sortOrder. Mirrors groupByRole's Map-then-sort shape
 *  verbatim (Corpus.tsx:1714-1725). */
export function groupAchievementsByRole(
  roles: RoleRecord[],
  achievements: AchievementRecord[],
): RoleGroup[] {
  const byRole = new Map<string | null, AchievementRecord[]>();
  for (const a of achievements) {
    if (!isLiveCard(a)) continue;
    const key = a.roleId ?? null;
    const arr = byRole.get(key) ?? [];
    arr.push(a);
    byRole.set(key, arr);
  }
  for (const arr of byRole.values()) arr.sort((x, y) => x.sortOrder - y.sortOrder);

  const ordered: RoleGroup[] = [];
  for (const role of roles) {
    const items = byRole.get(role.id);
    if (items?.length) ordered.push({ role, items });
  }
  const orphan = byRole.get(null);
  if (orphan?.length) ordered.push({ role: null, items: orphan });
  return ordered;
}

/** Role display order for the canvas: current role first, then reverse-
 *  chronological by startDate. Mirrors apps/web's sortedRoles (Corpus.tsx:362-
 *  370) — kept as a SEPARATE function from the grouping above (not folded into
 *  it) because that is exactly how apps/web's canvas uses the two: this order
 *  drives which role renders first; groupAchievementsByRole answers "what
 *  cards does this role have." A role with zero cards still appears (an empty
 *  era is real information — "you haven't told me about this yet" — not
 *  something to hide). */
export function orderRolesForCanvas(roles: RoleRecord[]): RoleRecord[] {
  return [...roles].sort(
    (a, b) =>
      (b.isCurrent === 1 ? 1 : 0) - (a.isCurrent === 1 ? 1 : 0) ||
      String(b.startDate ?? '').localeCompare(String(a.startDate ?? '')),
  );
}

/** Look up a role's card group by role id from the grouped result (the shape
 *  the canvas needs when it renders ordered roles against the grouping map —
 *  a role with no cards simply resolves to an empty array). */
export function itemsForRole(groups: RoleGroup[], roleId: string): AchievementRecord[] {
  return groups.find((g) => g.role?.id === roleId)?.items ?? [];
}

/** The unattached/orphaned bucket (roleId null) — cards imported or promoted
 *  without a home yet. Empty array when every card has a role. */
export function unattachedItems(groups: RoleGroup[]): AchievementRecord[] {
  return groups.find((g) => g.role === null)?.items ?? [];
}
